import { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config";

// Shared by the two estimators:
//
//   EarningsEstimator.js   /estimate — the standalone page. Takes an address,
//                          reads the road, and is the older of the two.
//   HostEstimator.js       the lead of "Become a host". No address at all: a
//                          business owner should be able to play with it
//                          before telling us who or where they are.
//
// Both POST the same endpoint and render the same numbers, so the money
// arithmetic and the option lists live here rather than in either of them.
// Two copies of `monthlyCents` would be two answers to the same question.

export const SIZES = [24, 32, 43, 50, 55, 65, 75];

export function money(cents) {
    if (cents === null || cents === undefined) return "—";
    return "$" + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
}

// Monthly take-home at a chosen occupancy.
//
// This REIMPLEMENTS utils/estimate.py's `monthly()`, deliberately, and the two
// must stay identical:
//
//     round(daily x days_per_month x occupancy x vendor_share) to the step
//
// Recomputed here rather than fetched because the occupancy control moves
// continuously and a request per drag would hammer an endpoint that costs two
// third-party lookups. Every input to the formula -- days_per_month,
// vendor_share and the rounding step -- is SENT BY THE SERVER in `assumptions`
// rather than hardcoded here, so the only thing duplicated is the shape of the
// arithmetic, not the numbers. tests/test_estimate.py pins the server's own
// figure at its default occupancy, and EarningsEstimator.test.js asserts this
// function reproduces it, so a change to one side that is not mirrored fails a
// test rather than quietly showing a host a different number than the API
// reports.
//
// Math.round vs Python's banker's rounding differs only when the value lands
// exactly on .5 of a step, which these float inputs never produce.
export function monthlyCents(dailyCents, occupancy, assumptions) {
    if (dailyCents === null || dailyCents === undefined) return null;
    const a = assumptions || {};
    const days = a.days_per_month || 30.4;
    const share = a.vendor_share === undefined ? 0.8 : a.vendor_share;
    const step = a.monthly_rounding_cents || 100;
    return Math.round((dailyCents * days * occupancy * share) / step) * step;
}

// How many days a month a given occupancy works out to.
//
// Every control that sets occupancy shows this rather than the percentage.
// "About 7 days a month" is something a salon owner can check against their
// own week; "23% occupancy" is a figure they have to translate first, and a
// number nobody can sanity-check is a number nobody argues with -- which is
// the opposite of what handing them this control is for.
export function bookedDays(occupancy, assumptions) {
    return Math.round(((assumptions || {}).days_per_month || 30.4) * occupancy);
}

// Fallbacks, used only until /api/estimate/options answers. Deliberately short:
// the server owns the real lists, and a long hardcoded copy here is one that
// silently goes stale when a venue type is added.
const FALLBACK_VENUES = [
    { value: "salon", label: "Salon, barbershop or nail salon", default_dwell: "30_60" },
    { value: "other", label: "Somewhere else", default_dwell: "15_30" },
];

// The venue types, visitor bands and dwell bands the form should offer.
//
// Served rather than hardcoded because the multipliers live in the backend's
// estimate.py, and a form offering a choice the model does not know gets
// silently mapped to "other" -- a wrong answer with no error anywhere.
export function useEstimatorOptions() {
    const [options, setOptions] = useState({
        venues: FALLBACK_VENUES,
        visitorBands: [],
        dwellBands: [],
        disclosure: "",
        coverage: null,
    });

    useEffect(() => {
        let alive = true;
        fetch(`${API_BASE_URL}/api/estimate/options`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (!alive || !d) return;
                setOptions((current) => ({
                    venues: (Array.isArray(d.venue_types) && d.venue_types.length)
                        ? d.venue_types
                        : current.venues,
                    visitorBands: Array.isArray(d.visitor_bands) ? d.visitor_bands : [],
                    dwellBands: Array.isArray(d.dwell_bands) ? d.dwell_bands : [],
                    disclosure: d.visitors_disclosure || "",
                    coverage: d.coverage || null,
                }));
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    return options;
}
